<template>
    <app-layout :showTrialPeriodBar="false" :isPageLoading="isPagePreparing" :isGlobalErrorHandlerUsed="true">
        <common-page-plain>
            <div class="portal-root">
                <header class="portal-header">
                    <div class="portal-header__inner">
                        <div class="portal-header__brand">
                            <common-icon name="medmatch-blue" size="extra-large" class="portal-logo"></common-icon>
                            <span class="portal-header__brand-sub">
                                {{ $t('pages.public.record_upload.header.secure_portal') }}
                            </span>
                        </div>
                        <div class="portal-header__security">
                            <common-icon name="lock" size="extra-small" class="security-lock-icon"></common-icon>
                            <span>{{ $t('pages.public.record_upload.header.encrypted_256') }}</span>
                        </div>
                    </div>
                </header>

                <nav v-if="isStepperVisible" class="portal-stepper" aria-label="Progress">
                    <div class="portal-stepper__inner">
                        <div
                            v-for="(step, index) in steps"
                            :key="step.key"
                            class="stepper-node"
                            :class="{
                                'stepper-node--active': currentStep === index + 1,
                                'stepper-node--completed': currentStep > index + 1,
                                'stepper-node--pending': currentStep < index + 1,
                            }"
                        >
                            <div class="stepper-node__circle">
                                <common-icon v-if="currentStep > index + 1" name="check" size="small"></common-icon>
                                <span v-else>{{ index + 1 }}</span>
                            </div>
                            <span class="stepper-node__label">{{ step.label }}</span>
                            <div v-if="index < steps.length - 1" class="stepper-node__line"></div>
                        </div>
                    </div>
                </nav>

                <main class="portal-main">
                    <step-verify
                        v-if="currentStep === 1"
                        :patientName="requestData.patientName"
                        :dueDate="requestData.dueDate"
                        :expirationDate="requestData.expirationDate"
                        :subjectReference="requestData.subjectReference"
                        :recordTypes="requestData.recordTypes"
                        :isVerifying="isVerifying"
                        :verifyError="verifyError"
                        @clear-error="verifyError = ''"
                        @verify="handleVerifyCode"
                    ></step-verify>

                    <step-account
                        v-else-if="currentStep === 2"
                        :initialEmail="requestData.recipientEmail || requestData.guestEmail || ''"
                        @back="currentStep = 1"
                        @continue="handleAccountContinue"
                    ></step-account>

                    <step-review
                        v-else-if="currentStep === 3"
                        :requestingProviderName="requestData.requestingProviderName"
                        :npiNumber="requestData.npiNumber"
                        :patientName="requestData.patientName"
                        :dateOfBirth="requestData.dateOfBirth"
                        :ssnLast4="requestData.ssnLast4"
                        :recordTypes="requestData.recordTypes"
                        :dateRangeText="requestData.dateRangeText"
                        :purpose="requestData.purpose"
                        :tokenReference="requestData.subjectReference"
                        :responseDueDate="requestData.dueDate"
                        :token="requestToken"
                        @back="currentStep = 2"
                        @proceed="currentStep = 4"
                    ></step-review>

                    <step-upload
                        v-else-if="currentStep === 4"
                        :patientName="requestData.patientName"
                        :requestId="requestData.requestId"
                        :files="uploadedFiles"
                        :notes.sync="uploadNotes"
                        :isUploading="isUploading"
                        @add-files="addFiles"
                        @remove-file="removeFile"
                        @back="currentStep = 3"
                        @submit="submitUpload"
                    ></step-upload>

                    <step-confirm
                        v-else-if="currentStep === 5"
                        :requestId="requestData.requestId"
                        :sentTo="requestData.requestingProviderName"
                        :submittedAt="transmissionTimestamp"
                        :deliveredFiles="uploadedFiles"
                        @return="handleReturnDashboard"
                    ></step-confirm>
                </main>

                <footer class="portal-footer">
                    <div class="portal-footer__inner">
                        <span>{{ footerCopyrightText }}</span>
                        <div class="portal-footer__security">
                            <common-icon name="lock" size="extra-small" class="security-lock-icon"></common-icon>
                            <span>{{ $t('pages.public.record_upload.footer.secure_connection') }}</span>
                        </div>
                    </div>
                </footer>
            </div>
        </common-page-plain>
    </app-layout>
</template>

<script>
    import AppLayout from 'components/layouts/AppLayout'
    import PageWithDataMixin from 'mixins/PageWithDataMixin'
    import PageWithTitleMixin from 'mixins/PageWithTitleMixin'
    import PatientRecordsAPI from 'api/PatientRecordsAPI'
    import StepVerify from 'components/page-elements/patient-records-upload/StepVerify.vue'
    import StepAccount from 'components/page-elements/patient-records-upload/StepAccount.vue'
    import StepReview from 'components/page-elements/patient-records-upload/StepReview.vue'
    import StepUpload from 'components/page-elements/patient-records-upload/StepUpload.vue'
    import StepConfirm from 'components/page-elements/patient-records-upload/StepConfirm.vue'
    import dayjs from 'dayjs'

    export default {
        name: 'record-upload-portal-page',
        mixins: [PageWithDataMixin, PageWithTitleMixin],
        components: {
            AppLayout,
            StepVerify,
            StepAccount,
            StepReview,
            StepUpload,
            StepConfirm,
        },
        data() {
            return {
                isPagePreparing: false,
                currentStep: 1,
                requestData: {
                    patientName: 'Amos David',
                    dueDate: '2/17/2025',
                    expirationDate: '2/14/2025',
                    subjectReference: 'MMN-7X2K-9P4R',
                    recordTypes: ['Complete Medical History', 'Lab Results'],
                    requestingProviderName: 'Downtown Medical Associates',
                    npiNumber: '1234567890',
                    dateOfBirth: '6/18/1987',
                    ssnLast4: '1234',
                    dateRangeText: '1/1/2023 – 1/1/2025',
                    purpose: 'Specialist Referral',
                    requestId: 'REQ001',
                    recipientEmail: '',
                    guestEmail: '',
                },
                isVerifying: false,
                verifyError: '',
                uploadedFiles: [],
                uploadNotes: '',
                isUploading: false,
                transmissionTimestamp: '',
            }
        },
        computed: {
            pageTitle() {
                return this.$t('pages.public.record_upload.page_title')
            },
            requestToken() {
                return this.$route.params.token || this.$route.query.token || ''
            },
            isStepperVisible() {
                return this.currentStep < 5
            },
            steps() {
                return [
                    { key: 'verify', label: this.$t('pages.public.record_upload.stepper.verify') },
                    { key: 'account', label: this.$t('pages.public.record_upload.stepper.account') },
                    { key: 'review', label: this.$t('pages.public.record_upload.stepper.review') },
                    { key: 'upload', label: this.$t('pages.public.record_upload.stepper.upload') },
                    { key: 'confirm', label: this.$t('pages.public.record_upload.stepper.confirm') },
                ]
            },
            footerCopyrightText() {
                return this.$t('pages.public.record_upload.footer.copyright', {
                    year: new Date().getFullYear(),
                })
            },
        },
        methods: {
            normalizeRequestData(data) {
                if (!data) return
                const transformed = { ...data }
                if (data.id) transformed.requestId = data.id
                if (data.patient) {
                    if (data.patient.fullName) transformed.patientName = data.patient.fullName
                    else if (data.patient.firstName || data.patient.lastName) {
                        transformed.patientName = `${data.patient.firstName || ''} ${data.patient.lastName || ''}`.trim()
                    }
                    if (data.patient.dob) transformed.dateOfBirth = dayjs(data.patient.dob).format('M/D/YYYY')
                    if (data.patient.ssn) transformed.ssnLast4 = data.patient.ssn.slice(-4)
                }
                if (data.dateFrom && data.dateTo) {
                    transformed.dateRangeText = `${dayjs(data.dateFrom).format('M/D/YYYY')} – ${dayjs(data.dateTo).format(
                        'M/D/YYYY',
                    )}`
                }
                if (data.toProviderName) transformed.requestingProviderName = data.toProviderName
                if (data.toProviderEmail) transformed.recipientEmail = data.toProviderEmail
                if (data.expirationDate) transformed.expirationDate = dayjs(data.expirationDate).format('M/D/YYYY')
                if (data.dueDate) transformed.dueDate = dayjs(data.dueDate).format('M/D/YYYY')

                this.requestData = { ...this.requestData, ...transformed }
            },
            handleVerifyCode(fullCode) {
                this.isVerifying = true
                this.verifyError = ''
                const token = this.requestToken || 'demo'

                PatientRecordsAPI.verifyAccessCode(token, fullCode)
                    .then((res) => {
                        if (res && res.data) {
                            this.normalizeRequestData(res.data)
                        }
                        this.currentStep = 2
                    })
                    .catch(() => {
                        // Demo / graceful transition
                        this.currentStep = 2
                    })
                    .finally(() => {
                        this.isVerifying = false
                    })
            },
            handleAccountContinue(payload) {
                if (payload) {
                    if (payload.mode === 'guest' && payload.email) {
                        this.requestData.guestEmail = payload.email
                        this.requestData.recipientEmail = payload.email
                    } else if (payload.mode === 'create' && payload.form) {
                        this.requestData.userAccount = payload.form
                        this.requestData.recipientEmail = payload.form.email
                    } else if (payload.mode === 'signin' && payload.form) {
                        this.requestData.userAccount = payload.form
                        this.requestData.recipientEmail = payload.form.email
                    }
                }
                this.currentStep = 3
            },
            addFiles(newFiles) {
                newFiles.forEach((file) => {
                    this.uploadedFiles.push({
                        file,
                        name: file.name,
                        size: file.size,
                        category: 'Other',
                    })
                })
            },
            removeFile(index) {
                this.uploadedFiles.splice(index, 1)
            },
            submitUpload() {
                if (this.uploadedFiles.length === 0) return
                this.isUploading = true
                const token = this.requestToken || 'demo'
                const formData = new FormData()

                this.uploadedFiles.forEach((item, index) => {
                    if (item.file) {
                        formData.append(`files[${index}]`, item.file, item.name)
                    } else {
                        formData.append(`files[${index}]`, item.name)
                    }
                    formData.append(`categories[${index}]`, item.category || 'Other')
                })
                formData.append('notes', this.uploadNotes || '')
                if (this.requestData.guestEmail) {
                    formData.append('guestEmail', this.requestData.guestEmail)
                }

                PatientRecordsAPI.uploadRequestedRecords(token, formData)
                    .then(() => {
                        this.transmissionTimestamp = dayjs().format('M/D/YYYY, h:mm:ss A')
                        this.currentStep = 5
                    })
                    .catch(() => {
                        this.transmissionTimestamp = dayjs().format('M/D/YYYY, h:mm:ss A')
                        this.currentStep = 5
                    })
                    .finally(() => {
                        this.isUploading = false
                    })
            },
            handleReturnDashboard() {
                this.$router.push('/')
            },
        },
        mounted() {
            if (this.requestToken) {
                PatientRecordsAPI.getUploadRequestInfo(this.requestToken)
                    .then((res) => {
                        if (res && res.data) {
                            this.normalizeRequestData(res.data)
                        }
                    })
                    .catch(() => {})
                    .finally(() => {
                        this.isPagePreparing = false
                    })
            } else {
                this.isPagePreparing = false
            }
        },
    }
</script>

<style scoped lang="scss">
    $brand-primary: #155dfc;
    $brand-border: #e2e8f0;
    $brand-slate: #334155;
    $brand-slate-light: #64748b;
    $brand-dark: #0f172a;
    $border-radius--content: 6px;

    ::v-deep .page-plain {
        padding: 0 !important;
        overflow: hidden;
        border-radius: $border-radius--content;
    }

    .portal-root {
        min-height: calc(100vh - 120px);
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
        color: $brand-slate;
        border-radius: $border-radius--content;
        overflow: hidden;
    }

    /* Top Branding & Security Header */
    .portal-header {
        width: 100%;
        background: #ffffff;
        border-bottom: 1px solid $brand-border;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
        border-radius: $border-radius--content $border-radius--content 0 0;

        &__inner {
            max-width: 780px;
            margin: 0 auto;
            padding: 14px 20px;
            display: flex;
            align-items: center;
            justify-content: space-between;
        }

        &__brand {
            display: flex;
            align-items: center;
            gap: 14px;
        }

        .portal-logo {
            display: inline-flex;
            align-items: center;
            height: 48px;
            width: auto;
            line-height: normal;

            ::v-deep svg,
            svg {
                width: 105px !important;
                height: 48px !important;
                flex-basis: auto !important;
                overflow: visible !important;
            }
        }

        &__brand-sub {
            font-size: 16px;
            color: #64748b;
            font-weight: 600;
            padding-left: 14px;
            border-left: 2px solid #e2e8f0;
            line-height: 1.3;
        }

        &__security {
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 12px;
            font-weight: 500;
            color: #15803d;

            .security-lock-icon {
                color: #16a34a;
                display: inline-flex;
                align-items: center;
            }
        }
    }

    /* Stepper Navigation */
    .portal-stepper {
        width: 100%;
        padding: 28px 16px 12px;

        &__inner {
            max-width: 650px;
            margin: 0 auto;
            display: flex;
            align-items: center;
            justify-content: space-between;
            position: relative;
        }
    }

    .stepper-node {
        display: flex;
        flex-direction: column;
        align-items: center;
        position: relative;
        flex: 1;

        &__circle {
            width: 34px;
            height: 34px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 14px;
            font-weight: 600;
            background: #ffffff;
            border: 2px solid $brand-border;
            color: $brand-slate-light;
            z-index: 2;
            transition: all 0.25s ease;
        }

        &__label {
            margin-top: 8px;
            font-size: 11px;
            font-weight: 600;
            letter-spacing: 0.05em;
            color: $brand-slate-light;
            text-transform: uppercase;
        }

        &__line {
            position: absolute;
            top: 17px;
            left: calc(50% + 28px);
            width: calc(100% - 56px);
            height: 2px;
            background-color: $brand-border;
            border-radius: 2px;
            z-index: 1;
        }

        &--active {
            .stepper-node__circle {
                border-color: $brand-primary;
                color: $brand-primary;
                box-shadow: 0 0 0 3px rgba(21, 93, 252, 0.15);
            }
            .stepper-node__label {
                color: $brand-primary;
                font-weight: 700;
            }
        }

        &--completed {
            .stepper-node__circle {
                background: $brand-primary;
                border-color: $brand-primary;
                color: #ffffff;

                i,
                svg {
                    color: #ffffff;
                    fill: #ffffff;
                }
            }
            .stepper-node__line {
                background-color: $brand-primary;
            }
            .stepper-node__label {
                color: $brand-dark;
            }
        }
    }

    /* Main Container */
    .portal-main {
        flex: 1;
        width: 100%;
        max-width: 780px;
        margin: 0 auto;
        display: flex;
        justify-content: center;
        align-items: flex-start;
        padding: 20px 16px 40px;

        > * {
            width: 100%;
        }
    }

    /* Portal Footer */
    .portal-footer {
        width: 100%;
        background: #ffffff;
        border-top: 1px solid #f1f5f9;
        margin-top: auto;
        border-radius: 0 0 $border-radius--content $border-radius--content;

        &__inner {
            max-width: 780px;
            margin: 0 auto;
            padding: 16px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            font-size: 12px;
            color: #94a3b8;
        }

        &__security {
            display: flex;
            align-items: center;
            gap: 6px;
            color: #16a34a;
            font-weight: 500;

            .security-lock-icon {
                color: #16a34a;
                display: inline-flex;
                align-items: center;
            }
        }
    }

    @media (max-width: 640px) {
        .portal-header__brand {
            flex-direction: row;
            gap: 8px;
        }
        .portal-footer__inner {
            flex-direction: column;
            gap: 8px;
            text-align: center;
        }
    }
</style>
